import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import {
  clampPage,
  paginationLabel,
  totalPages as countPages,
} from '../../../../utils/serverPagination';
import './StockistTable.css';

// ── Export helpers (mirrors ChartModal pattern) ───────────────────────────────
function exportCSV(data, filename = 'stockist-data.csv') {
  if (!data?.length) return;
  const cols = [
    'StockistName', 'CurrentMonthSales', 'Avg6MonthSales',
    'ChangePercentage', 'PerformanceFlag',
    'Month_1_Sales', 'Month_2_Sales', 'Month_3_Sales',
    'Month_4_Sales', 'Month_5_Sales', 'Month_6_Sales',
  ];
  const rows = data.map(r => cols.map(k => JSON.stringify(r[k] ?? '')).join(','));
  const csv  = [cols.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function exportPNG(el, filename = 'stockist-table.png') {
  if (!el) return;
  try {
    const canvas = await html2canvas(el, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
      logging: false,
    });
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename;
    a.click();
  } catch (e) {
    console.warn('[StockistTable] PNG export failed:', e);
  }
}

// ── Month keys matching backend column names ───────────────────────────────────
const MONTH_KEYS = [
  'Month_1_Sales', 'Month_2_Sales', 'Month_3_Sales',
  'Month_4_Sales', 'Month_5_Sales', 'Month_6_Sales',
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtVal = v => {
  if (v == null) return '—';
  const n = Number(v);
  if (isNaN(n)) return '—';
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtPct = v => {
  if (v == null) return '—';
  return `${Number(v).toFixed(1)}%`;
};

// "202503" → "Mar'25"  |  null/bad → "Now"
const fmtMonthLabel = ym => {
  if (!ym) return 'Now';
  const s = String(ym).replace('-', '');
  if (s.length !== 6) return 'Now';
  const year  = parseInt(s.slice(0, 4), 10);
  const month = parseInt(s.slice(4, 6), 10) - 1;
  const d = new Date(year, month, 1);
  if (isNaN(d.getTime())) return 'Now';
  return d.toLocaleString('en-IN', { month: 'short' }) + `'${String(year).slice(2)}`;
};

const getTrendValues = row => MONTH_KEYS.map(k => Number(row[k] ?? 0));

// ── Performance flag → style tier ─────────────────────────────────────────────
const FLAG_MAP = {
  'SUDDEN HIKE':   { tier: 'hike',      label: '⚡ Sudden Hike'   },
  'CRITICAL SLUMP':{ tier: 'drop',      label: '📉 Critical Slump' },
  'SUDDEN DROP':   { tier: 'drop',      label: '📉 Sudden Drop'   },
  'CONSISTENT':    { tier: 'good',      label: '✅ Consistent'    },
  'ON TARGET':     { tier: 'excellent', label: '🎯 On Target'     },
  'BELOW TARGET':  { tier: 'average',   label: '⚠ Below Target'  },
  'CRITICAL':      { tier: 'poor',      label: '🔴 Critical'      },
};

const getFlag = raw => {
  if (!raw) return { tier: 'na', label: '—' };
  const key = raw.trim().toUpperCase();
  return FLAG_MAP[key] ?? { tier: 'na', label: raw };
};

// ── Mini SVG Sparkline (clickable) ───────────────────────────────────────────
const Sparkline = ({ row, onClick }) => {
  const values  = getTrendValues(row);
  const hasData = values.some(v => v !== 0);

  if (!hasData) {
    return <span className="sparkline--empty" onClick={onClick}>—</span>;
  }

  const W = 72, H = 28, pad = 3;
  const max   = Math.max(...values);
  const min   = Math.min(...values);
  const range = max - min || 1;

  const pts = values.map((v, i) => ({
    x: pad + (i / (values.length - 1)) * (W - pad * 2),
    y: H - pad - ((v - min) / range) * (H - pad * 2),
  }));

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${pts[pts.length-1].x.toFixed(1)},${H} L${pts[0].x.toFixed(1)},${H} Z`;

  const rising = values[values.length - 1] >= values[0];
  const color  = rising ? 'var(--green,#16a34a)' : 'var(--red,#dc2626)';
  const last   = pts[pts.length - 1];
  const gradId = `sg-${(row.StockistName || '').replace(/\s+/g, '')}`;

  return (
    <svg
      className="sparkline sparkline--clickable"
      viewBox={`0 0 ${W} ${H}`}
      width={W} height={H}
      onClick={onClick}
      title="Click to view full chart"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r="2.8" fill={color} />
    </svg>
  );
};

// ── Canvas Bar Chart inside Modal ─────────────────────────────────────────────
const BarChart = ({ row }) => {
  const canvasRef = useRef(null);
  const values    = getTrendValues(row);

  // Build actual "Mon'YY" labels for the 6 months preceding CurrentMonth
  const labels = useMemo(() => {
    const cm = String(row.CurrentMonth || '').replace(/-/g, '');
    if (cm.length !== 6) return MONTH_KEYS.map((_, i) => `M${i + 1}`);
    const year  = parseInt(cm.slice(0, 4), 10);
    const month = parseInt(cm.slice(4, 6), 10); // 1-based
    // M1 is 6 months before current, M6 is 1 month before current
    return MONTH_KEYS.map((_, i) => {
      const offset = 6 - i; // 6,5,4,3,2,1
      const d = new Date(year, month - 1 - offset, 1);
      return d.toLocaleString('en-IN', { month: 'short' }) + `'${String(d.getFullYear()).slice(2)}`;
    });
  }, [row.CurrentMonth]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const W   = canvas.offsetWidth;
    const H   = canvas.offsetHeight;
    if (!W || !H) return;

    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    // ── Theme colours ──────────────────────────────────────────────────────
    const cs      = getComputedStyle(document.documentElement);
    const cText   = cs.getPropertyValue('--text-primary').trim()   || '#0f172a';
    const cMute   = cs.getPropertyValue('--text-muted').trim()     || '#64748b';
    const cBorder = cs.getPropertyValue('--border').trim()          || '#e2e8f0';
    const cGreen  = cs.getPropertyValue('--green').trim()           || '#16a34a';
    const cRed    = cs.getPropertyValue('--red').trim()             || '#dc2626';
    const cAccent = cs.getPropertyValue('--accent').trim()          || '#C81E1E';
    const cGold   = '#d97706';

    // ── Layout ─────────────────────────────────────────────────────────────
    const padL = 58, padR = 16, padT = 20, padB = 44;
    const cW   = W - padL - padR;
    const cH   = H - padT - padB;

    // ── Scale ──────────────────────────────────────────────────────────────
    const cmVal   = Number(row.CurrentMonthSales ?? 0);
    const allVals = [...values, cmVal];
    const dataMax = Math.max(...allVals, 0);
    const dataMin = Math.min(...allVals, 0);
    // Add 12% headroom above max so value labels don't clip
    const headroom = (dataMax - dataMin) * 0.12 || Math.abs(dataMax) * 0.12 || 1;
    const max   = dataMax + headroom;
    const min   = dataMin < 0 ? dataMin - headroom * 0.5 : 0;
    const range = max - min || 1;

    const toY = v => padT + cH - ((v - min) / range) * cH;

    // ── Grid lines ─────────────────────────────────────────────────────────
    const gridN = 4;
    for (let i = 0; i <= gridN; i++) {
      const v = min + (i / gridN) * (max - min);
      const y = toY(v);
      ctx.strokeStyle = cBorder;
      ctx.lineWidth   = 1;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(padL + cW, y); ctx.stroke();

      // Y-axis labels — only show if not too close to zero line
      const zy = toY(0);
      if (Math.abs(y - zy) > 10 || i === 0) {
        ctx.fillStyle = cMute;
        ctx.font      = '10px system-ui,sans-serif';
        ctx.textAlign = 'right';
        const absV = Math.abs(v);
        const lbl  = absV >= 100000 ? `${(v/100000).toFixed(1)}L`
                   : absV >= 1000   ? `${(v/1000).toFixed(1)}k`
                   : v.toFixed(0);
        ctx.fillText(lbl, padL - 6, y + 3.5);
      }
    }

    // ── Zero baseline ──────────────────────────────────────────────────────
    if (min < 0) {
      const zy = toY(0);
      ctx.strokeStyle = '#94a3b8';
      ctx.lineWidth   = 1;
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(padL, zy); ctx.lineTo(padL + cW, zy); ctx.stroke();
    }

    // ── Draw a single rounded bar ──────────────────────────────────────────
    const drawBar = (cx, val, barW, fillColor, isNow) => {
      if (barW < 1) return;
      const zy   = toY(0);
      const barH = Math.abs(toY(val) - zy);
      if (barH < 1) return;

      const x = cx - barW / 2;
      const y = val >= 0 ? zy - barH : zy;
      const r = Math.min(4, barW / 3, barH / 2);

      // Bar fill — solid, no glow
      ctx.shadowBlur = 0;
      ctx.fillStyle  = fillColor;
      ctx.globalAlpha = isNow ? 1 : 0.82;
      ctx.beginPath();
      if (val >= 0) {
        // Rounded top corners only
        ctx.moveTo(x + r, y);
        ctx.lineTo(x + barW - r, y);
        ctx.arcTo(x + barW, y, x + barW, y + r, r);
        ctx.lineTo(x + barW, y + barH);
        ctx.lineTo(x, y + barH);
        ctx.arcTo(x, y, x + r, y, r);
      } else {
        // Rounded bottom corners only
        ctx.moveTo(x, y);
        ctx.lineTo(x + barW, y);
        ctx.lineTo(x + barW, y + barH - r);
        ctx.arcTo(x + barW, y + barH, x + barW - r, y + barH, r);
        ctx.lineTo(x + r, y + barH);
        ctx.arcTo(x, y + barH, x, y + barH - r, r);
        ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;

      // Value label above/below bar — only if bar is tall enough to matter
      const absV = Math.abs(val);
      if (absV > 0) {
        const lbl = absV >= 100000 ? `${(val/100000).toFixed(1)}L`
                  : absV >= 1000   ? `${(val/1000).toFixed(1)}k`
                  : val.toFixed(0);
        ctx.fillStyle = isNow ? cAccent : cText;
        ctx.font      = isNow ? `bold 10px system-ui,sans-serif` : `10px system-ui,sans-serif`;
        ctx.textAlign = 'center';
        const labelY  = val >= 0 ? y - 5 : y + barH + 11;
        ctx.fillText(lbl, cx, labelY);
      }
    };

    // ── Draw bars ──────────────────────────────────────────────────────────
    const totalBars = values.length + 1;
    const step      = cW / totalBars;
    const barW      = Math.min(36, step * 0.55);

    values.forEach((v, i) => {
      const cx    = padL + step * i + step / 2;
      const color = v >= 0 ? cGreen : cRed;
      drawBar(cx, v, barW, color, false);
    });

    // "Now" bar — always accent colour regardless of positive/negative
    const nowCx = padL + step * values.length + step / 2;
    drawBar(nowCx, cmVal, barW, cAccent, true);

    // ── X-axis labels ──────────────────────────────────────────────────────
    const nowLabel = fmtMonthLabel(row.CurrentMonth);
    const labelY = H - padB + 16;
    values.forEach((_, i) => {
      const cx = padL + step * i + step / 2;
      ctx.fillStyle = cMute;
      ctx.font      = '10px system-ui,sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(labels[i], cx, labelY);
    });
    ctx.fillStyle = cAccent;
    ctx.font      = 'bold 10px system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(nowLabel, nowCx, labelY);

    // ── L6M Avg dashed line ────────────────────────────────────────────────
    const avgVal = Number(row.Avg6MonthSales ?? 0);
    if (avgVal !== 0) {
      const avgY = toY(avgVal);
      ctx.strokeStyle = cGold;
      ctx.lineWidth   = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.beginPath(); ctx.moveTo(padL, avgY); ctx.lineTo(padL + cW, avgY); ctx.stroke();
      ctx.setLineDash([]);

      // Avg label — right-aligned, with a small pill background
      const avgLbl = `Avg ${fmtVal(avgVal)}`;
      ctx.font = '10px system-ui,sans-serif';
      const tw = ctx.measureText(avgLbl).width;
      ctx.fillStyle   = 'rgba(255,255,255,0.85)';
      ctx.fillRect(padL + cW - tw - 8, avgY - 12, tw + 8, 14);
      ctx.fillStyle = cGold;
      ctx.textAlign = 'right';
      ctx.fillText(avgLbl, padL + cW, avgY - 2);
    }

  }, [row]);

  useEffect(() => {
    draw();
    const ro = new ResizeObserver(draw);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [draw]);

  return <canvas ref={canvasRef} className="chart-modal__canvas" />;
};

// ── Chart Modal ───────────────────────────────────────────────────────────────
const ChartModal = ({ row, onClose }) => {
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const chg = Number(row.ChangePercentage ?? 0);

  return (
    <div
      className="chart-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="chart-modal">
        <div className="chart-modal__header">
          <div className="chart-modal__title-wrap">
            <span className="chart-modal__name">{row.StockistName}</span>
            <span className={`perf-pill perf-pill--${getFlag(row.PerformanceFlag).tier}`}>
              {getFlag(row.PerformanceFlag).label}
            </span>
          </div>
          <div className="chart-modal__meta">
            <div className="chart-modal__stat">
              <span>Current Month</span>
              <strong>{fmtVal(row.CurrentMonthSales)}</strong>
            </div>
            <div className="chart-modal__stat">
              <span>L6M Avg</span>
              <strong>{fmtVal(row.Avg6MonthSales)}</strong>
            </div>
            <div className="chart-modal__stat">
              <span>Change</span>
              <strong className={chg >= 0 ? 'text-green' : 'text-red'}>
                {fmtPct(row.ChangePercentage)}
              </strong>
            </div>
          </div>
          <button className="chart-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="chart-modal__body">
          <BarChart row={row} />
        </div>
        <div className="chart-modal__footer">
          <span>Last 6 months (oldest → newest) · {fmtMonthLabel(row.CurrentMonth)} = current month</span>
          <span style={{ color: '#d97706', fontWeight: 600 }}>— L6M avg</span>
        </div>
      </div>
    </div>
  );
};

// ── Change Badge ──────────────────────────────────────────────────────────────
const ChangeBadge = ({ value }) => {
  const n   = Number(value ?? 0);
  const cls = n > 5 ? 'up' : n < -5 ? 'down' : 'neutral';
  return (
    <span className={`change-badge change-badge--${cls}`}>
      {cls === 'up' ? '▲' : cls === 'down' ? '▼' : '●'} {Math.abs(n).toFixed(1)}%
    </span>
  );
};

// ── Performance Pill ──────────────────────────────────────────────────────────
const PerformancePill = ({ flag }) => {
  const { tier, label } = getFlag(flag);
  return <span className={`perf-pill perf-pill--${tier}`}>{label}</span>;
};

// ── StockistTable ─────────────────────────────────────────────────────────────
const StockistTable = ({ data = [], loading = false, totalRows, page: serverPage, pageSize: serverPageSize, onPageChange }) => {
  const [search,     setSearch]     = useState('');
  const [sortKey,    setSortKey]    = useState('CurrentMonthSales');
  const [sortDir,    setSortDir]    = useState('desc');
  const [clientPage, setClientPage] = useState(1);
  const [modal,      setModal]      = useState(null);
  const tableRef = useRef(null);

  // Server-side pagination when onPageChange is provided
  const isServerPaginated = !!onPageChange;
  const PAGE_SIZE = isServerPaginated ? (serverPageSize || 25) : 10;
  const page = isServerPaginated ? (serverPage || 1) : clientPage;
  const setPage = isServerPaginated ? onPageChange : setClientPage;

  const columns = [
    {
      key: 'StockistName',
      label: 'Stockist',
      align: 'left',
      sortable: true,
      render: v => <span className="stockist-name">{v}</span>,
    },
    {
      key: 'CurrentMonthSales',
      label: 'Current Month',
      align: 'right',
      sortable: true,
      render: v => <span className="sales-val">{fmtVal(v)}</span>,
    },
    {
      key: 'Avg6MonthSales',
      label: 'L6M Avg',
      align: 'right',
      sortable: true,
      render: fmtVal,
    },
    {
      key: 'ChangePercentage',
      label: 'Change %',
      align: 'right',
      sortable: true,
      render: v => <ChangeBadge value={v} />,
    },
    {
      key: 'PerformanceFlag',
      label: 'Status',
      align: 'center',
      sortable: true,
      render: v => <PerformancePill flag={v} />,
    },
    {
      key: '_chart',
      label: '',
      align: 'center',
      sortable: false,
      render: (_, row) => (
        <button className="chart-btn" onClick={() => setModal(row)} title="View 6M chart">
          📊
        </button>
      ),
    },
  ];

  const handleSort = key => {
    if (!columns.find(c => c.key === key)?.sortable) return;
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
    if (!isServerPaginated) setClientPage(1);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data || []).filter(r =>
      !q || (r.StockistName || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
    });
  }, [filtered, sortKey, sortDir]);

  // Page arithmetic comes from the shared helper so the service layer and the table
  // agree on page counts and clamping (Req 6.3, 6.4).
  const recordCount = isServerPaginated ? (totalRows ?? 0) : sorted.length;
  const pageCount   = countPages(recordCount, PAGE_SIZE);
  const currentPage = clampPage(page, recordCount, PAGE_SIZE);
  const paged = isServerPaginated
    ? sorted
    : sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const stats = useMemo(() => {
    if (!filtered.length) return null;
    const hikeN          = filtered.filter(r => (r.PerformanceFlag || '').toUpperCase().includes('HIKE')).length;
    const dropN          = filtered.filter(r => (r.PerformanceFlag || '').toUpperCase().includes('DROP') || (r.PerformanceFlag || '').toUpperCase().includes('SLUMP')).length;
    const stockistsCount = isServerPaginated ? (totalRows || filtered.length) : (filtered[0]?.TotalCustomers ?? filtered.length);
    return { hikeN, dropN, stockistsCount, total: filtered.length };
  }, [filtered, isServerPaginated, totalRows]);

  if (loading) return (
    <div className="stockist-table">
      <div className="stockist-table__skeleton">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="stockist-skeleton__row">
            {[...Array(7)].map((_, j) => <div key={j} className="stockist-skeleton__cell" />)}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="stockist-table" ref={tableRef}>
      {modal && <ChartModal row={modal} onClose={() => setModal(null)} />}

      {/* Toolbar */}
      <div className="stockist-table__toolbar">
            <div className="stockist-search">
              <span className="stockist-search__icon">🔍</span>
              <input
                className="stockist-search__input"
                type="text"
                placeholder="Search stockist…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
              {search && (
                <button className="stockist-search__clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>

            {stats && (
              <div className="stockist-stats-row">
                <div className="stockist-stat">
                  <span className="stockist-stat__label">Stockists Count</span>
                  <span className="stockist-stat__value">{stats.stockistsCount}</span>
                </div>
                <div className="stockist-stat stockist-stat--divider" />
                <div className="stockist-stat">
                  <span className="stockist-stat__label">⚡ Sudden Hike</span>
                  <span className="stockist-stat__value stockist-stat__value--orange">{stats.hikeN}</span>
                </div>
                <div className="stockist-stat stockist-stat--divider" />
                <div className="stockist-stat">
                  <span className="stockist-stat__label">📉 Sudden Drop</span>
                  <span className="stockist-stat__value stockist-stat__value--red">{stats.dropN}</span>
                </div>
              </div>
            )}

            {/* ── Export buttons ── */}
            <div className="stockist-export-btns">
              <button
                className="cmodal__action-btn"
                onClick={() => exportCSV(sorted, 'stockist-performance.csv')}
                title="Export all rows as CSV"
              >
                ⬇ CSV
              </button>
              <button
                className="cmodal__action-btn"
                onClick={() => exportPNG(tableRef.current, 'stockist-performance.png')}
                title="Export table as PNG"
              >
                🖼 PNG
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="stockist-table__scroll">
            <table className="stockist-tbl">
              <thead>
                <tr>
                  <th className="stockist-tbl__th stockist-tbl__th--idx">#</th>
                  {columns.map(col => (
                    <th
                      key={col.key}
                      className={[
                        'stockist-tbl__th',
                        `stockist-tbl__th--${col.align}`,
                        col.sortable        ? 'stockist-tbl__th--sortable' : '',
                        sortKey === col.key ? 'stockist-tbl__th--active'   : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                      {col.sortable && (
                        <span className="sort-icon">
                          {sortKey === col.key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="stockist-tbl__empty">
                      {search ? `No stockists matching "${search}"` : 'No stockist data available'}
                    </td>
                  </tr>
                ) : (
                  paged.map((row, i) => {
                    const chg    = Number(row.ChangePercentage ?? 0);
                    const rowMod = chg > 20 ? 'stockist-tbl__row--hike' : chg < -20 ? 'stockist-tbl__row--drop' : '';
                    return (
                      <tr key={`${row.StockistName}-${i}`} className={`stockist-tbl__row ${rowMod}`}>
                        <td className="stockist-tbl__td stockist-tbl__td--idx">
                          {(currentPage - 1) * PAGE_SIZE + i + 1}
                        </td>
                        {columns.map(col => (
                          <td key={col.key} className={`stockist-tbl__td stockist-tbl__td--${col.align}`}>
                            {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination — the label always renders, including for a single page and for
              zero records, so page, total pages, and record count stay visible (Req 6.5).
              The nav buttons stay in place but disable when there is nowhere to go. */}
          <div className="stockist-table__pagination">
            <button
              className="page-btn"
              disabled={currentPage <= 1}
              onClick={() => setPage(currentPage - 1)}
            >
              ‹ Prev
            </button>
            <span className="page-info">
              {paginationLabel({ page: currentPage, pageSize: PAGE_SIZE, total: recordCount })}
            </span>
            <button
              className="page-btn"
              disabled={currentPage >= pageCount}
              onClick={() => setPage(currentPage + 1)}
            >
              Next ›
            </button>
          </div>
    </div>
  );
};

export default StockistTable;